import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

/**
 * SAB-288 Phase 7 — reveal overlay queue.
 *
 * Holds the in-flight bonus award + a one-shot ledger of ids that have
 * already played the overlay this session. Trigger surfaces (the manual
 * award flow + the activity feed's fresh-bonus detector in Phase 8) call
 * dispatch(award); the OverlayHost subscribes and renders the explosion.
 *
 * One-shot semantics: a given bonus_award.id only plays once per session.
 * Subsequent dispatches with the same id are no-ops. This is what stops
 * stale awards from re-firing on every refetch / route remount.
 */

export interface RevealAward {
  /** bonus_award.id — used as the one-shot key. */
  id: string;
  bonusName: string;
  amount: number;
  teamId: string;
  teamName: string;
  reason?: string | null;
  awardedForWeek?: number | null;
}

interface RevealContextValue {
  pending: RevealAward | null;
  dispatch: (award: RevealAward) => void;
  dismiss: () => void;
}

const RevealCtx = createContext<RevealContextValue | null>(null);

export function RevealProvider({ children }: { children: ReactNode }) {
  const [pending, setPending] = useState<RevealAward | null>(null);
  // Set of bonus_award ids already played in this session. Doesn't survive
  // a hard refresh; that's intentional for now (Phase 8 will revisit if
  // post-cron auto bonuses need durable suppression).
  const shownRef = useRef<Set<string>>(new Set());

  const dispatch = useCallback((award: RevealAward) => {
    if (shownRef.current.has(award.id)) return;
    shownRef.current.add(award.id);
    setPending(award);
  }, []);

  const dismiss = useCallback(() => {
    setPending(null);
  }, []);

  const value = useMemo<RevealContextValue>(
    () => ({ pending, dispatch, dismiss }),
    [pending, dispatch, dismiss],
  );

  return <RevealCtx.Provider value={value}>{children}</RevealCtx.Provider>;
}

/**
 * Required-context variant; throws if no provider. Use from surfaces that
 * must be wrapped (the bonus award flow and the OverlayHost).
 */
export function useReveal(): RevealContextValue {
  const ctx = useContext(RevealCtx);
  if (!ctx) {
    throw new Error('useReveal must be used within RevealProvider');
  }
  return ctx;
}

/**
 * Optional variant; returns null if no provider. Use from leaf components
 * that may render outside the provider (e.g. activity feed embedded in a
 * widget where reveal hasn't been wired yet) so they degrade gracefully.
 */
export function useRevealOptional(): RevealContextValue | null {
  return useContext(RevealCtx);
}
